/*
 * 誉展示会 / 床装飾フロリア見積もり
 * Shopify標準お問い合わせフォームへ、確定時点の床構成を引き継ぎます。
 * 3D・画像生成ライブラリ、外部フォームAPIは読み込みません。
 */
(() => {
  "use strict";

  const ELEMENT_NAME = "homare-floorea-quote";
  const STORAGE_PREFIX = "homare-floorea-quote:v1:";
  const QUOTE_QUERY = "floorea_quote";
  const STATE_HASH = "floorea";
  const MAX_AGE_MS = 24 * 60 * 60 * 1000;
  const RETURN_MAX_AGE_MS = 60 * 60 * 1000;
  const MAX_SNAPSHOT_CHARS = 1500000;
  const VARIANT_ID_PATTERN = /^(?:[1-9]\d{0,19})?$/;
  const QUOTE_ID_PATTERN = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/;
  const SHALLOW_NOTE = "奥行2.7mタイプの必要数量は面積換算の概算です。実際の敷設方法・必要数量の確認が必要です。";

  const LAYOUTS = [
    { id: "1", label: "1小間", w: 3, d: 3 },
    { id: "2", label: "2小間", w: 6, d: 3 },
    { id: "3", label: "3小間", w: 9, d: 3 },
    { id: "4", label: "4小間", w: 6, d: 6 },
    { id: "5", label: "5小間", w: 15, d: 3 },
    { id: "6", label: "6小間", w: 9, d: 6 },
    { id: "shallow3", label: "奥行2.7mタイプ・幅3m", w: 3, d: 2.7 },
    { id: "shallow6", label: "奥行2.7mタイプ・幅6m", w: 6, d: 2.7 }
  ];

  const floorArea = (layout) => Math.round(layout.w * layout.d * 100) / 100;
  const floorQuantity = (layout) => Math.ceil(floorArea(layout));
  const isText = (value) => typeof value === "string" && value.length <= 4000;
  const isMoney = (value) => value === null || (
    Number.isSafeInteger(value) && value >= 0 && value < 1000000000000000
  );

  function sameStoreURL(value) {
    const url = new URL(value, location.origin);
    if (
      url.origin !== location.origin || !/^https?:$/.test(url.protocol) ||
      url.username || url.password
    ) {
      throw new Error("同じストア内のページを設定してください。");
    }
    return url;
  }

  // フロリア専用形式。既存の展示会版の保存形式とは共有しません。
  function decodeState(value) {
    if (typeof value !== "string" || value.length > 160) return null;
    const parts = value.split("~");
    const layout = LAYOUTS.find((item) => item.id === parts[1]);
    if (
      parts.length !== 4 || parts[0] !== "1" || !layout ||
      !/^[01]$/.test(parts[2]) || !VARIANT_ID_PATTERN.test(parts[3])
    ) {
      return null;
    }
    return {
      layout,
      floorEnabled: parts[2] === "1",
      floorVariantId: parts[3]
    };
  }

  function money(value, currency) {
    if (value === null) return "未確定";
    return new Intl.NumberFormat("ja-JP", {
      style: "currency",
      currency
    }).format(value / 100);
  }

  function createdAtLabel(value) {
    return new Date(value).toLocaleString("ja-JP", {
      timeZone: "Asia/Tokyo"
    }) + "（日本時間）";
  }

  function warningsFor(snapshot, state) {
    const warnings = [...snapshot.warnings];
    if (state.layout.d === 2.7 && !warnings.some((value) => value.includes("2.7m"))) {
      warnings.push(SHALLOW_NOTE);
    }
    return [...new Set(warnings)];
  }

  // 構成はブラウザー内で保存された参考情報です。決済・価格保証には使用しません。
  function loadSnapshot(id) {
    if (!QUOTE_ID_PATTERN.test(id || "")) return null;
    try {
      const raw = sessionStorage.getItem(STORAGE_PREFIX + id);
      if (!raw || raw.length > MAX_SNAPSHOT_CHARS) return null;
      const snapshot = JSON.parse(raw);
      if (!snapshot || typeof snapshot !== "object") return null;
      const state = decodeState(snapshot.state);
      const now = Date.now();
      if (
        snapshot.version !== 1 || snapshot.id !== id || !state || !state.floorEnabled ||
        !Number.isFinite(snapshot.createdAt) || now - snapshot.createdAt > MAX_AGE_MS ||
        snapshot.createdAt > now + 60000 || !Array.isArray(snapshot.rows) ||
        snapshot.rows.length !== 1 || !/^[A-Z]{3}$/.test(snapshot.currency)
      ) {
        return null;
      }

      const row = snapshot.rows[0];
      if (
        !row || row.id !== "floorea" || !Number.isInteger(row.quantity) ||
        row.quantity !== floorQuantity(state.layout) ||
        ![row.label, row.productName, row.variantName, row.status,
          row.url, row.productId, row.variantId].every(isText) ||
        !VARIANT_ID_PATTERN.test(row.productId) || !VARIANT_ID_PATTERN.test(row.variantId) ||
        !isMoney(row.unit) || !isMoney(row.subtotal) ||
        row.subtotal !== (row.unit === null ? null : row.unit * row.quantity) ||
        (state.floorVariantId && row.variantId !== state.floorVariantId)
      ) {
        return null;
      }
      if (row.url) sameStoreURL(row.url);
      if (
        snapshot.total !== row.subtotal || !isMoney(snapshot.total) ||
        !isText(snapshot.taxNote) ||
        !(snapshot.fetchedAt == null || isText(snapshot.fetchedAt)) ||
        !Array.isArray(snapshot.warnings) || snapshot.warnings.length > 10 ||
        !snapshot.warnings.every(isText) || !isText(snapshot.replayURL)
      ) {
        return null;
      }
      const replayURL = sameStoreURL(snapshot.replayURL);
      const replayStates = new URLSearchParams(replayURL.hash.slice(1)).getAll(STATE_HASH);
      if (replayStates.length !== 1 || replayStates[0] !== snapshot.state) return null;
      if (
        typeof snapshot.image !== "string" ||
        (snapshot.image && !/^data:image\/jpeg;base64,[A-Za-z0-9+/=]+$/.test(snapshot.image))
      ) {
        return null;
      }
      return snapshot;
    } catch {
      return null;
    }
  }

  function mailPayload(snapshot) {
    const state = decodeState(snapshot.state);
    const row = snapshot.rows[0];
    const lines = [
      "フロリア見積もり依頼（参考価格）",
      "構成ID：" + snapshot.id,
      "確定日時：" + createdAtLabel(snapshot.createdAt),
      "小間：" + state.layout.label + "／間口 " + state.layout.w + "m × 奥行 " + state.layout.d + "m",
      "床装飾：フロリア",
      "床面積：" + floorArea(state.layout) + "㎡",
      "必要数量：" + floorQuantity(state.layout) + "商品（1商品＝1㎡、床面積を整数に切り上げ）",
      "",
      row.label + "：" + row.productName,
      "床柄：" + (row.variantName || "バリエーション未確定"),
      "数量：" + row.quantity + "商品",
      "単価：" + money(row.unit, snapshot.currency),
      "小計：" + money(row.subtotal, snapshot.currency)
    ];
    if (row.status) lines.push("確認事項：" + row.status);
    if (row.productId) lines.push("商品ID：" + row.productId);
    if (row.variantId) lines.push("バリエーションID：" + row.variantId);
    if (row.url) lines.push("商品リンク：" + sameStoreURL(row.url).href);
    lines.push(
      "",
      "参考合計：" + money(snapshot.total, snapshot.currency),
      snapshot.taxNote,
      "通貨：" + snapshot.currency,
      "正式な価格・送料・納期は確認後にご案内します。"
    );
    if (snapshot.fetchedAt) lines.push("商品情報取得日時：" + snapshot.fetchedAt);
    const warnings = warningsFor(snapshot, state);
    if (warnings.length) lines.push("床装飾の確認事項：" + warnings.join(" "));
    lines.push(
      "",
      "フロリア3D再表示リンク：",
      snapshot.replayURL,
      "※再表示時の商品・バリエーション・価格は、その時点のテーマ設定から取得します。依頼時の商品明細は上記をご確認ください。"
    );
    return lines.join("\n");
  }

  class HomareFlooreaQuote extends HTMLElement {
    connectedCallback() {
      if (this._life) return;
      this._life = new AbortController();
      const signal = this._life.signal;
      const query = (selector) => this.querySelector(selector);
      const form = this.closest("form");
      const status = query("[data-quote-status]");
      const fieldset = query("fieldset");
      const requestedId = new URL(location.href).searchParams.get(QUOTE_QUERY);
      const returnKey = STORAGE_PREFIX + "return:" + location.pathname;

      if (this.dataset.formState === "success") {
        query("[data-quote-summary]").hidden = true;
        query("[data-quote-form-body]").hidden = true;
        status.hidden = true;
        try { sessionStorage.removeItem(returnKey); } catch { /* 保存不可でも完了表示を維持 */ }
        return;
      }

      let id = requestedId;
      // Shopify標準フォームの入力エラーでクエリがなくなった場合だけ、直前の構成へ戻します。
      if (!id && this.dataset.formState === "error") {
        try {
          const savedReturn = JSON.parse(sessionStorage.getItem(returnKey));
          if (
            savedReturn && Number.isFinite(savedReturn.at) &&
            savedReturn.at <= Date.now() + 60000 && Date.now() - savedReturn.at < RETURN_MAX_AGE_MS
          ) {
            id = savedReturn.id;
          }
        } catch { /* 構成なしの案内へ進む */ }
      }

      const snapshot = loadSnapshot(id);
      this._quote = snapshot;
      if (!snapshot) {
        status.textContent = requestedId || this.dataset.formState === "error"
          ? "構成を読み込めませんでした。有効期限（24時間）が切れた場合や別のタブで開いた場合は、フロリア3Dページからもう一度お進みください。"
          : "フロリア3Dシミュレーターで床装飾を選び、「この内容で見積もりを依頼する」からお進みください。";
        return;
      }

      const state = decodeState(snapshot.state);
      status.hidden = true;
      query("[data-quote-summary]").hidden = false;
      query("[data-quote-form-body]").hidden = false;
      query("[data-quote-layout]").textContent = state.layout.label + "（" + state.layout.w + "m × " + state.layout.d + "m）";
      query("[data-quote-coverage]").textContent = "床面積 " + floorArea(state.layout) + "㎡ ／ 必要数量 " + floorQuantity(state.layout) + "商品（1商品＝1㎡）";
      query("[data-quote-time]").textContent = createdAtLabel(snapshot.createdAt);
      query("[data-quote-id]").textContent = "構成ID：" + snapshot.id;
      for (const link of this.querySelectorAll("[data-quote-edit]")) {
        link.href = snapshot.replayURL;
        link.target = "_self";
      }
      const image = query("[data-quote-image]");
      const imageNote = query("[data-quote-image-note]");
      if (snapshot.image) {
        image.src = snapshot.image;
        image.hidden = false;
        imageNote.hidden = true;
        image.addEventListener("error", () => {
          image.hidden = true;
          imageNote.hidden = false;
        }, { once: true, signal });
      } else {
        imageNote.hidden = false;
      }

      const list = query("[data-quote-lines]");
      list.replaceChildren();
      for (const row of snapshot.rows) {
        const item = document.createElement("li");
        const label = document.createElement("p");
        const product = document.createElement(row.url ? "a" : "strong");
        const variant = document.createElement("p");
        const calculation = document.createElement("p");
        label.className = "hfq-item-label";
        label.textContent = row.label;
        product.textContent = row.productName;
        if (row.url) {
          product.href = sameStoreURL(row.url).href;
          product.target = "_self";
        }
        variant.className = "hfq-note";
        variant.textContent = "床柄：" + (row.variantName || "未確定");
        calculation.className = "hfq-calc";
        calculation.textContent = "単価 " + money(row.unit, snapshot.currency) + " × " + row.quantity + "商品 = " + money(row.subtotal, snapshot.currency);
        item.append(label, product, variant, calculation);
        if (row.status) {
          const rowWarning = document.createElement("p");
          rowWarning.className = "hfq-warning";
          rowWarning.textContent = row.status;
          item.append(rowWarning);
        }
        list.append(item);
      }
      query("[data-quote-total]").textContent = money(snapshot.total, snapshot.currency);
      query("[data-quote-tax]").textContent = snapshot.taxNote;
      const warnings = warningsFor(snapshot, state);
      if (snapshot.total === null) {
        warnings.unshift("価格を確認できていません。金額は担当者が確認してご案内します。");
      }
      query("[data-quote-warnings]").textContent = warnings.join(" ");
      query("[data-quote-warnings]").hidden = warnings.length === 0;
      query("[data-quote-payload]").value = mailPayload(snapshot);
      fieldset.disabled = false;

      // 正常時はpreventDefaultしません。必須入力・ShopifyのPOST・スパム対策を維持します。
      form.addEventListener("submit", (event) => {
        if (!loadSnapshot(snapshot.id)) {
          event.preventDefault();
          status.hidden = false;
          status.textContent = "構成の有効期限が切れたか、保存データを確認できません。フロリア3Dページからもう一度お進みください。";
          status.scrollIntoView({ block: "center" });
          fieldset.disabled = true;
          return;
        }
        query("[data-quote-payload]").value = mailPayload(snapshot);
        try {
          sessionStorage.setItem(returnKey, JSON.stringify({ id: snapshot.id, at: Date.now() }));
        } catch { /* Shopifyの送信自体は妨げない */ }
      }, { signal });
    }

    disconnectedCallback() {
      queueMicrotask(() => {
        if (!this.isConnected) {
          this._life?.abort();
          this._life = null;
          this._quote = null;
        }
      });
    }
  }

  if (!customElements.get(ELEMENT_NAME)) {
    customElements.define(ELEMENT_NAME, HomareFlooreaQuote);
  }
})();
